import React, { useState, useEffect } from "react";
import cx from "classnames";
import styles from "../scss/AFQuestionForm.module.scss";
import AppFormField from "./AppFormField";
import { Form, Formik } from "formik";
import { IAnswerProps, IAppFormQuestion } from "../interfaces/IAppFormData";
import { getControlType, needFieldBorders, showHelpText } from "../shared/AppFormHelper";
import { ControlType } from "../enums/ControlType";
import Tooltip from "../../common/components/Tooltips/Tooltip";
import { useAppFormUtils } from "../hooks/useAppFormUtils";
import AFNavigation from "./AFNavigation";

interface IAppQuestionForm {
    applicantName: string;
    question: IAppFormQuestion;
    nextQuestion: (any) => void;
    handleBack: (e) => void;
    skipIsVisible: boolean;
    handleSkip?: () => void;
    showSkipDialog?: boolean;
}

const AppQuestionForm: React.FC<IAppQuestionForm> = (props) => {
    const { question, nextQuestion, applicantName, handleBack, skipIsVisible, handleSkip, showSkipDialog } = props;
    const [questionReady, setQuestionReady] = useState(false);
    const [formAnswer, setFormAnswer] = useState<IAnswerProps[]>([]);
    const [formReadyToSubmit, setFormReadyToSubmit] = useState(false);
    const [searchTimeout, setSearchTimeout] = useState<ReturnType<typeof setTimeout>>(
        null
    );

    const { checkFormList } = useAppFormUtils();

    const handleAnswers = (params): void => {
        const answerParams = [
            {
                questionId: params?.questionId,
                disclosure:
                    params?.disclosure && params.disclosure?.length
                        ? params.disclosure
                        : [],
                answer: params?.answer || "",
                questionNumber: question?.questionNumber
            }
        ];
        setFormReadyToSubmit(checkFormList([question], answerParams));
        if (getControlType(question) === ControlType.RADIOBUTTON && !question?.answer) {
            const timeout = setTimeout(() => {
                nextQuestion(answerParams);
            }, 500);

            setSearchTimeout(timeout);
            return;
        }
        setFormAnswer(answerParams);
    };

    const handleNextQuestion = (answerParams): void => {
        if (!formReadyToSubmit) return;
        nextQuestion(answerParams);
    };

    const handleButtonVisibility = (controlType: number): boolean => {
        switch (controlType) {
            case ControlType.RADIOBUTTON:
                const buttonStatus = !!question?.answer || false;
                return buttonStatus;
            default:
                return true;
        }
    };

    useEffect(() => {
        return () => {
            clearTimeout(searchTimeout);
        };
    }, []);

    return (
        <div
            className={cx(
                styles["app-question-form"]
            )}>
            {questionReady && (
                <React.Fragment>          
                    <div
                        className={cx(styles["form-title"], {
                            "gap-2xl": !!question?.disclosureName
                        })}>
                        <label
                            className={styles["form-title-client"]}
                            dangerouslySetInnerHTML={{
                                __html: applicantName
                            }}></label>
                    </div>                  
                    <div
                        className={cx(styles["form-title"], {
                            "gap-2xl": !!question?.disclosureName
                        })}>
                        <label
                            className={styles["form-title-label"]}
                            htmlFor={question?.disclosureName || question?.questionLabel}
                            dangerouslySetInnerHTML={{
                                __html:
                                    question?.disclosureName ||
                                    question?.questionLabel ||
                                    ""
                            }}></label>
                        {!!showHelpText(question) && (
                            <Tooltip
                                className={styles["form-title-tooltip"]}
                                tooltipContent={<div dangerouslySetInnerHTML={{ __html: showHelpText(question) }} />}
                            />
                        )}
                    </div>
                </React.Fragment>
            )}

            <Formik
                initialValues={{}}
                onSubmit={() => {
                    handleNextQuestion(formAnswer);
                }}>
                {() => (
                    <Form autoComplete="off" className={styles["fa-question-form"]}>
                        <div
                            className={cx(
                                "layout-flex-column-left",
                                styles["question-form"]
                            )}>
                            {!!question?.disclosureName && (
                                <label
                                    className={styles["question-form-label"]}
                                    htmlFor={question?.questionLabel}
                                    dangerouslySetInnerHTML={{
                                        __html: question?.questionLabel || ""
                                    }}></label>
                            )}
                            <div
                                className={cx([
                                    styles["question-form-field"],
                                    needFieldBorders(question)
                                        ? styles["field-borders"]
                                        : ""
                                ])}>
                                <AppFormField
                                    question={question}
                                    answerChanges={handleAnswers}
                                    fieldReady={setQuestionReady}
                                    labelText={
                                        question?.questionLabel ||
                                        question?.disclosureName ||
                                        ""
                                    }
                                />
                            </div>
                        </div>
                        <AFNavigation
                            handleBack={handleBack}
                            skipIsVisible={skipIsVisible}
                            showSkipDialog={showSkipDialog}
                            handleSkip={handleSkip}
                            forwardIsVisible={handleButtonVisibility(getControlType(question))}
                            forwardIsDisabled={!formReadyToSubmit}
                            forwardType="submit"
                            forwardText={"Next"}
                        />
                    </Form>
                )}
            </Formik>
        </div>
    );
};

export default AppQuestionForm;
